import { useState } from "react";

const principles = [
  { icon: "ri-award-line", title: "质量优先", desc: "只推画风、剧情、人物全在线的作品" },
  { icon: "ri-eye-line", title: "本命标准", desc: "按『能当本命』的严苛标准筛出入坑作" },
  { icon: "ri-hand-heart-line", title: "真实口碑", desc: "结合千万读者真实反馈，口碑不打折" },
];

const channels = [
  { icon: "ri-mail-line", label: "邮箱", value: "hello@131mh.example.com" },
  { icon: "ri-wechat-2-line", label: "公众号", value: "131漫画网" },
  { icon: "ri-bilibili-fill", label: "哔哩哔哩", value: "@131漫画网" },
];

export default function AboutContact() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const hp = (data.get("company_alt") as string) || "";
    if (hp.trim()) {
      setStatus("success");
      form.reset();
      return;
    }
    hp && data.delete("company_alt");

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      const v = String(value);
      if (v) payload.append(key, v);
    });

    setStatus("idle");
    setFormError("");
    try {
      const res = await fetch(
        "https://readdy.ai/api/form/da25kobk8r5te5mqshj0",
        {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: payload.toString(),
        },
      );
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta as Record<string, unknown>) || {};
      const code = parsed?.code as string | undefined;
      const serverMsg =
        (meta?.message as string) || (parsed?.message as string) || (meta?.detail as string) || "";

      if (!res.ok || code !== "OK" || /spam|form data is spam/i.test(responseText + serverMsg)) {
        setStatus("error");
        setFormError(
          serverMsg && !/spam|form data is spam/i.test(serverMsg)
            ? serverMsg
            : "提交未成功，请稍后再试。",
        );
        return;
      }
      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    }
  };

  return (
    <section id="about" className="py-12 md:py-20 bg-background-50">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="text-center max-w-2xl mx-auto mb-8 md:mb-12">
          <span className="inline-flex items-center gap-1.5 text-primary-600 text-sm font-medium">
            <i className="ri-information-line"></i>
            关于我们
          </span>
          <h2 className="mt-2 font-display text-2xl md:text-4xl text-foreground-950">
            131漫画网，只为帮你找到本命
          </h2>
          <p className="mt-2 text-sm md:text-base text-foreground-600">
            我们相信每一部值得追的漫画，都该被认真推荐。131漫画网致力于成为最懂你的本命推荐入坑作指南。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
          <div className="flex flex-col gap-5">
            <div className="p-5 md:p-6 rounded-xl bg-background-100/70 border border-background-200/70">
              <h3 className="font-display text-lg text-foreground-950 flex items-center gap-2">
                <i className="ri-focus-3-line text-primary-500"></i>
                我们的定位与理念
              </h3>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                从画风、剧情、世界观到人物塑造，我们以『能当本命』的标准为每一位读者把关。
                拒绝无意义的水作推荐，只把真正经得起反复回味、值得一生推的入坑作，认真交到你手上。
              </p>
              <div className="mt-4 flex flex-col gap-3">
                {principles.map((p) => (
                  <div key={p.title} className="flex items-start gap-3">
                    <span className="w-9 h-9 shrink-0 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
                      <i className={`${p.icon} text-lg`}></i>
                    </span>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{p.title}</div>
                      <div className="text-xs text-foreground-600 mt-0.5">{p.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-5 md:p-6 rounded-xl bg-background-100/70 border border-background-200/70">
              <h3 className="font-display text-lg text-foreground-950 flex items-center gap-2">
                <i className="ri-phone-line text-secondary-600"></i>
                联系我们
              </h3>
              <p className="mt-2 text-sm text-foreground-600">
                安利你心中的本命漫，或对推荐有任何建议，都欢迎随时找到我们。
              </p>
              <div className="mt-4 flex flex-col gap-3">
                {channels.map((c) => (
                  <div key={c.label} className="flex items-center gap-3 text-sm">
                    <span className="w-9 h-9 shrink-0 rounded-lg bg-secondary-100 text-secondary-800 flex items-center justify-center">
                      <i className={`${c.icon} text-lg`}></i>
                    </span>
                    <span className="text-foreground-500">{c.label}</span>
                    <span className="text-foreground-900 font-medium">{c.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="p-5 md:p-7 rounded-xl bg-background-100/70 border border-background-200/70">
            <h3 className="font-display text-xl text-foreground-950">安利你的本命漫</h3>
            <p className="mt-1 text-sm text-foreground-600">
              把你的本命推荐给我们，审核通过后有机会登上推荐列表，让更多同好发现它。
            </p>

            <form onSubmit={onSubmit} data-readdy-form="131漫画网联系表单" className="mt-5 flex flex-col gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="name" className="text-sm text-foreground-700">你的昵称</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/80 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="email" className="text-sm text-foreground-700">联系邮箱</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/80 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="topic" className="text-sm text-foreground-700">留言类型</label>
                <select
                  id="topic"
                  name="topic"
                  className="px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/80 text-sm text-foreground-900 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                >
                  <option value="推荐本命">推荐本命漫</option>
                  <option value="合作建议">合作建议</option>
                  <option value="意见反馈">意见反馈</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="message" className="text-sm text-foreground-700">留言内容</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  placeholder="写下你推荐的本命漫和理由……"
                  className="px-3 py-2.5 rounded-lg bg-background-50 border border-background-200/80 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                ></textarea>
                <span className="text-xs text-foreground-400 text-right">最多 500 字</span>
              </div>

              <div className="auxiliary-entry" aria-hidden="true">
                <label htmlFor="company_alt">公司</label>
                <input
                  id="company_alt"
                  name="company_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {status === "success" && (
                <p className="text-sm text-secondary-700 bg-secondary-100 px-3 py-2.5 rounded-lg">
                  <i className="ri-checkbox-circle-line mr-1.5"></i>
                  已收到你的留言，我们会尽快回复，感谢你安利本命！
                </p>
              )}
              {status === "error" && (
                <p className="text-sm text-primary-700 bg-primary-100 px-3 py-2.5 rounded-lg">
                  <i className="ri-error-warning-line mr-1.5"></i>
                  {formError}
                </p>
              )}

              <button
                type="submit"
                className="px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-send-plane-line mr-1.5"></i>
                提交留言
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}